import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, Baby, AlertCircle, Heart } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CycleSettingsProps {
  onSettingsChange?: () => void;
  onJourneyChange?: (journey: 'menstrual' | 'pregnancy' | 'perimenopause' | 'menopause') => void;
}

export default function CycleSettings({ onSettingsChange, onJourneyChange }: CycleSettingsProps) {
  const [isPregnancyMode, setIsPregnancyMode] = useState(false);
  const [dueDate, setDueDate] = useState<string>('');
  const [defaultCycleLength, setDefaultCycleLength] = useState(28);
  const [lutealLength, setLutealLength] = useState(14);
  const [ovulationDay, setOvulationDay] = useState(14);
  const [ignoreShortCycles, setIgnoreShortCycles] = useState(1);
  const [ignoreLongCycles, setIgnoreLongCycles] = useState(0);
  const [alwaysUseDefault, setAlwaysUseDefault] = useState(false);
  const [useLutealPrediction, setUseLutealPrediction] = useState(false);
  const [isPerimenopausal, setIsPerimenopausal] = useState(false);
  const [isMenopausal, setIsMenopausal] = useState(false);
  
  // TTC and IVF tracking
  const [tryingToConceive, setTryingToConceive] = useState(false);
  const [ivfCycleActive, setIvfCycleActive] = useState(false);
  const [ivfCycleDay, setIvfCycleDay] = useState<number | undefined>();
  const [fertilityTreatmentType, setFertilityTreatmentType] = useState<string>('');
  const [postpartumMode, setPostpartumMode] = useState(false);
  const [babyBirthDate, setBabyBirthDate] = useState<string>('');
  const [ivfSupportActivities, setIvfSupportActivities] = useState({
    acupuncture: false,
    chinese_medicine: false,
    meditation: false,
    visualisation: false,
    future_timeline_journey: false,
    conscious_connection: false,
    kinesiology: false,
    massage: false,
    breathwork: false,
    nutrition_plan: false,
    supplements: false
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        // Load pregnancy mode settings
        const pregnancyMode = (data as any).pregnancy_mode || false;
        const dueDateStr = (data as any).pregnancy_due_date;
        
        setIsPregnancyMode(pregnancyMode);
        if (dueDateStr) setDueDate(dueDateStr);
        
        // Load cycle settings
        setDefaultCycleLength(data.cycle_length || 28);
        setLutealLength((data as any).luteal_length || 14);
        setOvulationDay((data as any).ovulation_day || 14);
        setIgnoreShortCycles((data as any).ignore_cycles_less_than || 1);
        setIgnoreLongCycles((data as any).ignore_cycles_more_than || 0);
        setAlwaysUseDefault((data as any).always_use_default_cycle || false);
        setUseLutealPrediction((data as any).use_luteal_prediction || false);
        setIsPerimenopausal((data as any).is_perimenopausal || false);
        setIsMenopausal((data as any).is_menopausal || false);
        
        // Load TTC/IVF settings
        setTryingToConceive((data as any).trying_to_conceive || false);
        setIvfCycleActive((data as any).ivf_cycle_active || false);
        setIvfCycleDay((data as any).ivf_cycle_day);
        setFertilityTreatmentType((data as any).fertility_treatment_type || '');
        
        // Load postpartum settings
        setPostpartumMode((data as any).postpartum_mode || false);
        setBabyBirthDate((data as any).baby_birth_date || '');
        
        // Load IVF support activities
        if ((data as any).ivf_support_activities) {
          setIvfSupportActivities((data as any).ivf_support_activities);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please log in to save settings');
        return;
      }

      const today = format(new Date(), 'yyyy-MM-dd');

      const updateData = {
        user_id: user.id,
        date: today,
        pregnancy_mode: isPregnancyMode,
        pregnancy_due_date: dueDate || null,
        cycle_length: defaultCycleLength,
        luteal_length: lutealLength,
        ovulation_day: ovulationDay,
        ignore_cycles_less_than: ignoreShortCycles,
        ignore_cycles_more_than: ignoreLongCycles,
        always_use_default_cycle: alwaysUseDefault,
        use_luteal_prediction: useLutealPrediction,
        is_perimenopausal: isPerimenopausal,
        is_menopausal: isMenopausal,
        trying_to_conceive: tryingToConceive,
        ivf_cycle_active: ivfCycleActive,
        ivf_cycle_day: ivfCycleDay || null,
        fertility_treatment_type: fertilityTreatmentType || null,
        postpartum_mode: postpartumMode,
        baby_birth_date: babyBirthDate || null,
        ivf_support_activities: ivfSupportActivities,
      };

      const { error } = await supabase
        .from('daily_checkins')
        .upsert(updateData, {
          onConflict: 'user_id,date'
        });

      if (error) throw error;

      toast.success('Cycle settings saved successfully');
      onSettingsChange?.();
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Advanced Cycle Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Pregnancy Mode */}
        <div className="space-y-4 p-4 border rounded-lg bg-gradient-to-br from-pink-500/5 to-purple-500/5">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="pregnancy-mode" className="text-base font-semibold flex items-center gap-2">
                <Baby className="h-4 w-4" />
                Pregnancy Mode
              </Label>
              <p className="text-sm text-muted-foreground">
                Pauses period predictions and tracks countdown to due date
              </p>
              <p className="text-xs text-accent font-medium mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Enabling this will automatically switch you to the Pregnancy Journey
              </p>
            </div>
            <Switch
              id="pregnancy-mode"
              checked={isPregnancyMode}
              onCheckedChange={(checked) => {
                setIsPregnancyMode(checked);
                if (checked) {
                  onJourneyChange?.('pregnancy');
                  toast.success('Switched to Pregnancy Journey', {
                    description: 'Your tracker has been updated to pregnancy mode.'
                  });
                } else {
                  onJourneyChange?.('menstrual');
                  toast.success('Switched to Menstrual Cycle Journey', {
                    description: 'Your tracker has been updated to menstrual cycle mode.'
                  });
                }
              }}
            />
          </div>
          
          {isPregnancyMode && (
            <>
              <div>
                <Label htmlFor="due-date">Due Date</Label>
                <Input
                  id="due-date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  When pregnancy mode is active, period tracking is paused
                </p>
              </div>
              
              <div className="space-y-4 mt-4 p-4 border rounded-lg bg-gradient-to-br from-blue-500/5 to-purple-500/5">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="postpartum-mode" className="text-base font-semibold flex items-center gap-2">
                      <Baby className="h-4 w-4" />
                      Switch to Postpartum Mode
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Track your postpartum recovery and baby's early days
                    </p>
                    <p className="text-xs text-accent font-medium mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Enabling this will switch you to the Postpartum Journey
                    </p>
                  </div>
                  <Switch
                    id="postpartum-mode"
                    checked={postpartumMode}
                    onCheckedChange={(checked) => {
                      setPostpartumMode(checked);
                      if (checked) {
                        onJourneyChange?.('menstrual');
                        toast.success('Switched to Postpartum Journey', {
                          description: 'Congratulations! Track your recovery and baby\'s milestones.'
                        });
                      }
                    }}
                  />
                </div>
                
                {postpartumMode && (
                  <div>
                    <Label htmlFor="birth-date">Baby's Birth Date</Label>
                    <Input
                      id="birth-date"
                      type="date"
                      value={babyBirthDate}
                      onChange={(e) => setBabyBirthDate(e.target.value)}
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This helps us provide age-appropriate milestones and guidance
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Cycle Length Settings */}
        <div className="space-y-4">
          <h3 className="font-semibold">Cycle Length Predictions</h3>
          
          <div>
            <Label htmlFor="default-cycle">Default Cycle Length (days)</Label>
            <Input
              id="default-cycle"
              type="number"
              min="21"
              max="45"
              value={defaultCycleLength}
              onChange={(e) => setDefaultCycleLength(Number(e.target.value))}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Your average cycle length. We use your last 3 cycles to predict future periods.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="always-default">Always Use Default Cycle</Label>
              <p className="text-xs text-muted-foreground">
                Force the app to use default cycle length for predictions
              </p>
            </div>
            <Switch
              id="always-default"
              checked={alwaysUseDefault}
              onCheckedChange={setAlwaysUseDefault}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ignore-short">Ignore Cycles Less Than (days)</Label>
              <Input
                id="ignore-short"
                type="number"
                min="0"
                max="20"
                value={ignoreShortCycles}
                onChange={(e) => setIgnoreShortCycles(Number(e.target.value))}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="ignore-long">Ignore Cycles More Than (days)</Label>
              <Input
                id="ignore-long"
                type="number"
                min="0"
                max="60"
                value={ignoreLongCycles}
                onChange={(e) => setIgnoreLongCycles(Number(e.target.value))}
                placeholder="None"
                className="mt-1"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            You can choose to ignore certain cycles that fall beyond your average range
          </p>
        </div>

        {/* TTC and IVF Tracking */}
        <div className="space-y-4 p-4 border rounded-lg bg-gradient-to-br from-rose-500/5 to-pink-500/5">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="ttc-mode" className="text-base font-semibold flex items-center gap-2">
                <Heart className="h-4 w-4" />
                Trying to Conceive Mode
              </Label>
              <p className="text-sm text-muted-foreground">
                Enhanced ovulation tracking and fertility insights
              </p>
            </div>
            <Switch
              id="ttc-mode"
              checked={tryingToConceive}
              onCheckedChange={setTryingToConceive}
            />
          </div>

          {tryingToConceive && (
            <div className="space-y-4 pl-4 border-l-2 border-rose-200 dark:border-rose-800">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="ivf-cycle" className="text-base font-semibold">
                    IVF Cycle Active
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Track your IVF cycle and medications
                  </p>
                </div>
                <Switch
                  id="ivf-cycle"
                  checked={ivfCycleActive}
                  onCheckedChange={setIvfCycleActive}
                />
              </div>

              {ivfCycleActive && (
                <>
                  <div>
                    <Label htmlFor="ivf-day">IVF Cycle Day</Label>
                    <Input
                      id="ivf-day"
                      type="number"
                      min="1"
                      max="60"
                      value={ivfCycleDay || ''}
                      onChange={(e) => setIvfCycleDay(e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="Enter current cycle day"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      What day of your IVF cycle are you on?
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="treatment-type">Fertility Treatment Type</Label>
                    <Select value={fertilityTreatmentType} onValueChange={setFertilityTreatmentType}>
                      <SelectTrigger id="treatment-type" className="mt-1">
                        <SelectValue placeholder="Select treatment type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ivf">IVF (In Vitro Fertilization)</SelectItem>
                        <SelectItem value="iui">IUI (Intrauterine Insemination)</SelectItem>
                        <SelectItem value="ovulation_induction">Ovulation Induction</SelectItem>
                        <SelectItem value="egg_freezing">Egg Freezing</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* IVF Support Activities */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Support Activities for IVF Success</Label>
                    <p className="text-sm text-muted-foreground">
                      Track holistic practices shown to support IVF outcomes
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="acupuncture"
                          checked={ivfSupportActivities.acupuncture}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, acupuncture: checked }))
                          }
                        />
                        <Label htmlFor="acupuncture" className="text-sm cursor-pointer">Acupuncture</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="chinese-medicine"
                          checked={ivfSupportActivities.chinese_medicine}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, chinese_medicine: checked }))
                          }
                        />
                        <Label htmlFor="chinese-medicine" className="text-sm cursor-pointer">Chinese Medicine</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="meditation"
                          checked={ivfSupportActivities.meditation}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, meditation: checked }))
                          }
                        />
                        <Label htmlFor="meditation" className="text-sm cursor-pointer">Meditation</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="visualisation"
                          checked={ivfSupportActivities.visualisation}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, visualisation: checked }))
                          }
                        />
                        <Label htmlFor="visualisation" className="text-sm cursor-pointer">Visualisation</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="future-timeline"
                          checked={ivfSupportActivities.future_timeline_journey}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, future_timeline_journey: checked }))
                          }
                        />
                        <Label htmlFor="future-timeline" className="text-sm cursor-pointer">Future Timeline</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="conscious-connection"
                          checked={ivfSupportActivities.conscious_connection}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, conscious_connection: checked }))
                          }
                        />
                        <Label htmlFor="conscious-connection" className="text-sm cursor-pointer">Calling Child In</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="kinesiology"
                          checked={ivfSupportActivities.kinesiology}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, kinesiology: checked }))
                          }
                        />
                        <Label htmlFor="kinesiology" className="text-sm cursor-pointer">Kinesiology</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="massage"
                          checked={ivfSupportActivities.massage}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, massage: checked }))
                          }
                        />
                        <Label htmlFor="massage" className="text-sm cursor-pointer">Massage</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="breathwork"
                          checked={ivfSupportActivities.breathwork}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, breathwork: checked }))
                          }
                        />
                        <Label htmlFor="breathwork" className="text-sm cursor-pointer">Breathwork</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="nutrition"
                          checked={ivfSupportActivities.nutrition_plan}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, nutrition_plan: checked }))
                          }
                        />
                        <Label htmlFor="nutrition" className="text-sm cursor-pointer">Nutrition Plan</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="supplements"
                          checked={ivfSupportActivities.supplements}
                          onCheckedChange={(checked) => 
                            setIvfSupportActivities(prev => ({ ...prev, supplements: checked }))
                          }
                        />
                        <Label htmlFor="supplements" className="text-sm cursor-pointer">Supplements</Label>
                      </div>
                    </div>
                  </div>

                  <Alert className="border-accent/20 bg-accent/5">
                    <AlertCircle className="h-4 w-4 text-accent" />
                    <AlertDescription className="text-sm">
                      Track medications, injections, appointments, and support activities in your daily check-in and calendar for comprehensive monitoring.
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </div>
          )}
        </div>

        {/* Ovulation & Luteal Settings */}
        <div className="space-y-4">
          <h3 className="font-semibold">Ovulation Tracking</h3>
          
          <div>
            <Label htmlFor="ovulation-day">Default Ovulation Day</Label>
            <Input
              id="ovulation-day"
              type="number"
              min="7"
              max="21"
              value={ovulationDay}
              onChange={(e) => setOvulationDay(Number(e.target.value))}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Days before your expected period (typically 14 days)
            </p>
          </div>

          <div>
            <Label htmlFor="luteal-length">Luteal Phase Length (days)</Label>
            <Input
              id="luteal-length"
              type="number"
              min="10"
              max="16"
              value={lutealLength}
              onChange={(e) => setLutealLength(Number(e.target.value))}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Number of days between ovulation and your period (typically 12-14 days)
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="use-luteal">Predict Periods Using Luteal Length</Label>
              <p className="text-xs text-muted-foreground">
                Once ovulation is detected, use luteal length for more accurate predictions
              </p>
            </div>
            <Switch
              id="use-luteal"
              checked={useLutealPrediction}
              onCheckedChange={setUseLutealPrediction}
            />
          </div>
        </div>

        {/* Perimenopause/Menopause Toggle */}
        <div className="space-y-4 p-4 border rounded-lg bg-gradient-to-br from-purple-500/5 to-pink-500/5">
          <h3 className="font-semibold">Life Stage Settings</h3>
          
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="perimenopause-mode" className="text-base font-semibold">
                Perimenopause Mode
              </Label>
              <p className="text-sm text-muted-foreground">
                Track irregular cycles during perimenopause transition
              </p>
              <p className="text-xs text-accent font-medium mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Enabling this will automatically switch you to the Perimenopause Journey
              </p>
            </div>
            <Switch
              id="perimenopause-mode"
              checked={isPerimenopausal}
              onCheckedChange={(checked) => {
                setIsPerimenopausal(checked);
                if (checked) {
                  onJourneyChange?.('perimenopause');
                  toast.success('Switched to Perimenopause Journey', {
                    description: 'Your tracker has been updated to perimenopause mode.'
                  });
                } else {
                  onJourneyChange?.('menstrual');
                  toast.success('Switched to Menstrual Cycle Journey', {
                    description: 'Your tracker has been updated to menstrual cycle mode.'
                  });
                }
              }}
            />
          </div>

          {isPerimenopausal && (
            <>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Perimenopause mode helps track irregular cycles. If you've had no period for 12+ months, consider switching to menopause mode.
                </AlertDescription>
              </Alert>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="menopause-mode" className="text-base font-semibold">
                    Switch to Menopause Mode
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    No period for 12+ months? Focus on lunar cycle tracking
                  </p>
                  <p className="text-xs text-accent font-medium mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Enabling this will automatically switch you to the Menopause Journey
                  </p>
                </div>
                <Switch
                  id="menopause-mode"
                  checked={isMenopausal}
                  onCheckedChange={(checked) => {
                    setIsMenopausal(checked);
                    if (checked) {
                      onJourneyChange?.('menopause');
                      toast.success('Switched to Menopause Journey', {
                        description: 'Your tracker has been updated to menopause mode.'
                      });
                    } else {
                      onJourneyChange?.('perimenopause');
                      toast.success('Switched to Perimenopause Journey', {
                        description: 'Your tracker has been updated to perimenopause mode.'
                      });
                    }
                  }}
                />
              </div>

              {isMenopausal && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Menopause mode hides menstrual cycle colors and focuses on lunar phases for continued rhythm and wisdom.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </div>

        <Button onClick={saveSettings} className="w-full" size="lg">
          Save All Settings
        </Button>
      </CardContent>
    </Card>
  );
}
